import {
  DateField,
  FieldSave,
  ResponseError,
  SignerAppApi,
  SignerAppReadyResponse,
  TextField,
} from '@dropbox/sign-internal-client';
import * as signerApi from 'signer-app/context/signer-app-client/signer-api';
import { simulateNetworkDelay } from 'signer-app/context/signer-app-client/simulate-network-delay';
import {
  autofillAndConditionalLogic,
  checkboxes0to1,
  everyTextValidation,
  exampleSignatureRequest,
  multipleSignaturesAndInitials,
  previousSignerFields,
  signaturesWithTextField,
  simpleFields,
  singleZenPage,
  varietyOfFields,
  veryTallPage,
} from 'signer-app/signer-experience/story-data';
import { ThemeName } from 'signer-app/white-labeling/themes';
import { Chance } from 'chance';
import invariant from 'invariant';

/**
 * The real endpoint uses GUIDs, but for testing purposes we can name a scenario
 * and use it as an ID.
 *
 * Traditionally you might setup your mocks inside tests around which specific
 * things you expect your test to call. That ties your test to implementation
 * details and doesn't allow you to reuse those mocks across tests. And then you
 * get to Storybook where the recommended soution is mock service worker. So
 * that's mocking at the HTTP layer instead of the API layer.
 *
 * To avoid the problems above, we use a mock implementation of our type-safe
 * API. The tests and storybook use the same mock client. A real client would lookup
 * IDs in a database, but the mock client just codes them into the implementation.
 *
 * **NOTE**: We allow a dot-separated string of the above IDs to be passed in
 * to allow mocks to be combined, but it's too expensive to model it as a
 * TypeScript union.
 */
export type MockTransmissionGUID =
  | 'default'
  | 'very-tall-page'
  | 'access-code'
  | 'attachment'
  | 'decline'
  | 'long-load'
  | 'field-types'
  | 'checkboxes-0-1'
  | 'checkboxes-1'
  | 'autofill-and-conditional-logic'
  | 'text-field-validation'
  | 'optional-validation'
  | 'mobile-ready'
  | 'esign-disclosure'
  | 'esign-disclosure-opted-in'
  | 'ready-immediate'
  | 'ready-delayed-long-load'
  | 'signature-timestamp'
  | 'finalize-trusted-internal-redirect-with-write-back'
  | 'finalize-trusted-external-redirect'
  | 'finalize-untrusted-external-redirect'
  | 'finalize-postsign'
  | 'date-testing'
  | 'eid-at-finalize'
  | 'eid-pending'
  | 'user-untrusted'
  | 'unexpected-500'
  | 'ready-error'
  | 'prefill'
  | `theme-${ThemeName}`
  | (string & {});

export const defaultEsignDisclosure: signerApi.EsignDisclosure = {
  enabled: false,
  id: 0,
  businessName: '',
  additionalText: '',
  userOptIn: false,
};

type DeclineArgs = {
  transmissionGuid?: string;
  declineReason: string;
  accessCode?: string | null;
  isSmsDelivery?: boolean;
};

type DeclineResponse = {
  redirectUrl: string;
  success: boolean;
};

const buildReadyRequest = (tsmGuid: string, token?: string) => {
  const start = Date.now();
  /**
   * Most requests have already been prepared before the signer has the link.
   * The first call to ready will be `ready: true` and then the get endpoint can
   * be called.
   */
  let end = start;
  if (tsmGuid.includes('long-load')) {
    end = start + 10000;
  }

  const pagesTotal = 50;
  return (): SignerAppReadyResponse => {
    const now = Date.now();
    // Calculate progress
    const ready = now >= end;
    const progress = ready
      ? 1
      : /**
         * if start and end are the same, this divides by 0
         */
        Math.min((now - start) / Math.min(1, end - start), 1);

    return {
      token: token ?? Math.random().toString(36).slice(2),
      ready,
      isInFlightEditing: false,
      pagesConverted: Math.floor(progress * pagesTotal),
      pagesSynced: Math.floor(progress * pagesTotal),
      pagesTotal,
      successfulTags: [],
    };
  };
};

/**
 * Seeds a set of properties for a signature request that can be attached to
 * the response object and referenced for assertions in tests.
 *
 * @example
 * ```
 * test('my thing', () => {
 *   const { transmissionGuid } = getSeededValues('default');
 *
 *   render(<stories.main signatureRequestId='default' />);
 *
 *   // The response in the mock will contain the seeded `transmissionGuid` value
 *   expect(someApiCall).toHaveBeenCalledWith({
 *     transmissionGuid,
 *   });
 * });
 * ```
 */
export function getSeededValues(id: MockTransmissionGUID) {
  const chance = new Chance(id);
  const signatureRequestId = `mock-id-${chance.guid()}`;
  idMap.set(signatureRequestId, id);
  return {
    signatureRequestId,
  };
}
const idMap = new Map<string, string>();

const getTsmFromSignatureRequestId = (signatureRequestId: string) => {
  const transmissionId = idMap.get(signatureRequestId);
  invariant(
    transmissionId,
    `No transmission ID found for ${signatureRequestId}`,
  );
  return transmissionId;
};

export class SignerAppApiMock extends SignerAppApi {
  tries: Record<string, number> = {};

  signerAppReadyRequest: SignerAppApi['signerAppReadyRequest'] = async (
    tsmGuid,
    token,
  ): Promise<SignerAppReadyResponse> => {
    if (tsmGuid.includes('ready-error')) {
      throw new ResponseError(
        new Response(JSON.stringify({ error: 'Internal Server Error' }), {
          status: 500,
        }),
      );
    }

    this.readyRequests[tsmGuid] ??= buildReadyRequest(tsmGuid, token);

    const response = this.readyRequests[tsmGuid]();
    return response;
  };

  readyRequests: Record<string, ReturnType<typeof buildReadyRequest>> = {};

  signerAppSignatureRequestGetRequest: SignerAppApi['signerAppSignatureRequestGetRequest'] =
    async (signatureRequestId, accessCode) => {
      const tsmGuid = getTsmFromSignatureRequestId(signatureRequestId);
      const seededValues = getSeededValues(tsmGuid);
      invariant(
        seededValues.signatureRequestId === signatureRequestId,
        `signatureRequestId ${signatureRequestId} does not match ${seededValues.signatureRequestId}`,
      );

      await simulateNetworkDelay();
      let signatureRequest = { ...exampleSignatureRequest };

      if (tsmGuid.includes('test')) {
        signatureRequest.testMode = true;
      }

      if (tsmGuid.includes('previous-signer-fields')) {
        signatureRequest.fields = previousSignerFields;
      }

      if (tsmGuid.includes('access-code')) {
        if (accessCode !== '12345') {
          if (this.tries[tsmGuid] >= 3) {
            throw new ResponseError(
              {
                status: 429,
                statusText: 'Too Many Requests',
              } as Response,
              'Response returned an error code',
            );
          }

          this.tries[tsmGuid] = (this.tries[tsmGuid] || 0) + 1;
          // authorization needed
          throw new ResponseError(
            {
              status: 403,
              statusText: 'Unauthorized',
            } as Response,
            'Response returned an error code',
          );
        }
      }

      if (tsmGuid.includes('user-untrusted')) {
        throw new ResponseError(
          new Response(
            JSON.stringify({
              error: 'user cannot access this guid',
            }),
            { status: 401 },
          ),
        );
      }

      if (tsmGuid.includes('unexpected-500')) {
        throw new ResponseError(
          new Response(
            JSON.stringify({
              error: 'Internal Server Error',
            }),
            { status: 500 },
          ),
        );
      }

      if (tsmGuid.includes('attachmentUploaded')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.attachmentSlots = [
          {
            id: 'attachment-1',
            signer: 1,
            name: 'Resume',
            instructions: 'Please upload your resume',
            required: true,
            fileName: 'Resume.pdf',
            uploadedAt: new Date().toISOString(),
          },
        ];
      } else if (tsmGuid.includes('optional-attachments-uploaded')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.attachmentSlots = [
          {
            id: 'attachment-1',
            signer: 1,
            name: 'Resume',
            instructions: 'Please upload your resume',
            required: false,
            fileName: 'Resume.pdf',
            uploadedAt: new Date().toISOString(),
          },
          {
            id: 'attachment-2',
            signer: 1,
            name: 'Drivers License',
            instructions: 'Please upload your drivers license',
            required: false,
            fileName: 'License.pdf',
            uploadedAt: new Date().toISOString(),
          },
          {
            id: 'attachment-3',
            signer: 1,
            name: 'pet photo',
            instructions: 'Upload a photo of your pet (optional)',
            required: false,
            uploadedAt: null,
          },
        ];
      } else if (tsmGuid.includes('optional-attachments')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.attachmentSlots = [
          {
            id: 'attachment-1',
            signer: 1,
            name: 'Resume',
            instructions: 'Please upload your resume',
            required: false,
            uploadedAt: null,
          },
          {
            id: 'attachment-2',
            signer: 1,
            name: 'Drivers License',
            instructions: 'Please upload your drivers license',
            required: false,
            uploadedAt: null,
          },
          {
            id: 'attachment-3',
            signer: 1,
            name: 'pet photo',
            instructions: 'Upload a photo of your pet (optional)',
            required: false,
            uploadedAt: null,
          },
        ];
      } else if (tsmGuid.includes('attachment')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.attachmentSlots = [
          {
            id: 'attachment-1',
            signer: 1,
            name: 'Resume',
            instructions: 'Please upload your resume',
            required: true,
            uploadedAt: null,
          },
          {
            id: 'attachment-2',
            signer: 1,
            name: 'Drivers License',
            instructions: 'Please upload your drivers license',
            required: true,
            uploadedAt: null,
          },
          {
            id: 'attachment-3',
            signer: 1,
            name: 'pet photo',
            instructions: 'Upload a photo of your pet (optional)',
            required: false,
            uploadedAt: null,
          },
        ];
      }

      if (tsmGuid.includes('simple')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = simpleFields;
      }

      if (
        tsmGuid.includes('single-signature') ||
        tsmGuid.includes('single-initials')
      ) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = [
          {
            id: 'single-signature',
            pageIndex: 0,
            x: 43,
            y: 45,
            required: true,
            linkId: null,
            type: 'signature',
            signature: tsmGuid.includes('filled')
              ? {
                  guid: 'doctor-who',
                }
              : undefined,
            width: 72,
            height: 12,
            signer: '1',
            documentId: '51c3faa49c0b5ae09455c518b4c3558a5015e393',
            name: 'Signature',
            integrationMetadata: null,
          },
        ];
        if (tsmGuid.includes('single-initials')) {
          signatureRequest.fields[0].type = 'initials';
        }
      }
      if (tsmGuid.includes('single-email')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = [
          {
            id: 'single-email',
            pageIndex: 0,
            x: 43,
            y: 45,
            required: true,
            linkId: null,
            type: 'text',
            width: 72,
            height: 12,
            lineHeight: 12,
            signer: '1',
            fontFamily: 'notoSans',
            documentId: '51c3faa49c0b5ae09455c518b4c3558a5015e393',
            name: 'Email',
            integrationMetadata: null,
            validationType: 'email_address',
          },
        ];
        if (tsmGuid.includes('filled')) {
          (signatureRequest.fields[0] as TextField).value =
            'lauren@example.com';
        }
      }

      const user: ResponseType['user'] = {
        settings: {
          firstName: 'Default',
          lastName: 'User',
        },
        primarySignatureGuid: undefined,
      };

      const isEmbedded = false;
      const canInsertEverywhere = true;
      const allowColorSignature = false;
      let defaultDateFormat = 'MM/DD/YYYY';

      if (tsmGuid.includes('date-testing')) {
        defaultDateFormat = 'YYYY-MM-DD';
        const baseDateField: DateField = {
          id: 'date-field',
          fontSize: 12,
          pageIndex: 0,
          x: 488,
          y: 175,
          required: true,
          linkId: null,
          type: 'date',
          width: 104,
          height: 18,
          dateFormat: 'MM / DD / YYYY',
          signer: '1',
          fontFamily: 'arial',
          documentId: '173b3c2dfb3e161b2368e8f911105cead9c21dc9',
          name: 'DateSigned1',
          integrationMetadata: null,
          placeholder: '',
        };
        signatureRequest.fields = [
          {
            ...baseDateField,
            id: 'date-field-1',
            // Should use the default date format
            dateFormat: '',
            x: 10,
            y: 10,
          },
          {
            // Should use the format on the field
            ...baseDateField,
            id: 'date-field-2',
            dateFormat: 'MM/DD/YYYY',
            x: 10,
            y: 30,
          },
          {
            ...baseDateField,
            id: 'date-field-3',
            // Should use whatever is here
            value: 'already-formatted',
            x: 10,
            y: 50,
          },
        ];
      }

      if (tsmGuid.includes('previous-signer-date-fields')) {
        defaultDateFormat = 'MM / DD / YYYY';
        const baseDateField: DateField = {
          id: 'date-field',
          fontSize: 12,
          pageIndex: 0,
          x: 488,
          y: 175,
          required: true,
          linkId: null,
          type: 'date',
          width: 104,
          height: 18,
          dateFormat: 'MM / DD / YYYY',
          signer: '1',
          fontFamily: 'arial',
          documentId: '173b3c2dfb3e161b2368e8f911105cead9c21dc9',
          name: 'DateSigned1',
          integrationMetadata: null,
          placeholder: '',
        };
        signatureRequest.fields = [
          {
            ...baseDateField,
            id: 'previous-signer-date',
            name: 'DateSigned1',
            readOnly: true,
            value: '01/15/2024',
            signer: '0',
            x: 10,
            y: 10,
          },
          {
            ...baseDateField,
            id: 'current-signer-date',
            name: 'DateSigned2',
            signer: '1',
            x: 10,
            y: 30,
          },
        ];
      }

      if (tsmGuid.includes('autofill-and-conditional-logic')) {
        signatureRequest = {
          ...signatureRequest,
          ...autofillAndConditionalLogic,
        };
      }

      if (tsmGuid.includes('very-tall-page')) {
        signatureRequest = { ...veryTallPage };
      } else if (tsmGuid.includes('text-field-validation')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = everyTextValidation.map((field) => ({
          ...field,
          documentId: signatureRequest.pages[0].documentId,
        }));
      } else if (tsmGuid.includes('signatures-with-textfield')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = signaturesWithTextField.map((field) => ({
          ...field,
          documentId: signatureRequest.pages[0].documentId,
        }));
      } else if (tsmGuid.includes('multiple-signatures-initials')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = multipleSignaturesAndInitials.map(
          (field) => ({
            ...field,
            documentId: signatureRequest.pages[0].documentId,
          }),
        );
      } else if (tsmGuid.includes('field-types')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = varietyOfFields.map((field) => ({
          ...field,
          documentId: signatureRequest.pages[0].documentId,
        }));
      } else if (tsmGuid.includes('checkboxes-0-1')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = checkboxes0to1.map((field) => ({
          ...field,
          documentId: signatureRequest.pages[0].documentId,
        }));
      } else if (tsmGuid.includes('checkboxes-1')) {
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = checkboxes0to1.map((field) => ({
          ...field,
          requirement: 'require_1',
          documentId: signatureRequest.pages[0].documentId,
        }));
      } else if (tsmGuid.includes('optional-validation')) {
        signatureRequest.pages = singleZenPage;
        const phoneNumber = everyTextValidation.find(
          (f) => f.id === 'validation-phone-number',
        ) as TextField;
        const numbersOnly = everyTextValidation.find(
          (f) => f.id === 'validation-numbers-only',
        ) as TextField;
        const lettersOnly = everyTextValidation.find(
          (f) => f.id === 'validation-letters-only',
        ) as TextField;

        signatureRequest.fields = [
          phoneNumber,
          numbersOnly,
          {
            ...lettersOnly,
            required: false,
          },
        ].map((field, index) => {
          return {
            ...field,
            y: 30 + index * 30,
          };
        });
      }

      if (tsmGuid.includes('long-names')) {
        const chance = new Chance(tsmGuid);

        signatureRequest.fields = signatureRequest.fields.map((field) => ({
          ...field,
          name: `${field.name} (${chance.sentence({
            words: 15,
          })})`,
        }));
      }

      signatureRequest.fields = signatureRequest.fields.map((field) => {
        return {
          ...field,
          apiId: `apiId-${field.id}`,
        };
      });

      if (
        tsmGuid.includes('finalize-') ||
        tsmGuid.includes('eid-at-finalize') ||
        tsmGuid.includes('error-on-finalize')
      ) {
        // Easier to test this way with fewer fields
        signatureRequest.pages = singleZenPage;
        signatureRequest.fields = checkboxes0to1.map((field) => ({
          ...field,
          documentId: signatureRequest.pages[0].documentId,
        }));
      }

      if (tsmGuid.includes('signature-timestamp')) {
        signatureRequest.signerOptions = {
          ...signatureRequest.signerOptions,
          signatureDateTimestamp: true,
        } satisfies ResponseType['signatureRequest']['signerOptions'];

        signatureRequest.fields = signatureRequest.fields.map((field) => {
          return {
            ...field,
            signerName: 'Lauren Ipsum',
          };
        });
      }

      const esignDisclosure = tsmGuid.includes('esign-disclosure')
        ? {
            enabled: true,
            id: 1,
            businessName: 'Test Business',
            additionalText: 'This is a test',
            userOptIn: tsmGuid.includes('esign-disclosure-opted-in'),
          }
        : defaultEsignDisclosure;

      type ResponseType = Awaited<
        ReturnType<SignerAppApi['signerAppSignatureRequestGetRequest']>
      >;
      const signatureRequestObject: ResponseType = {
        isMobileReady: tsmGuid.includes('mobile-ready'),
        transmissionGuid: tsmGuid,
        requestTitle: 'Example Request Title',
        signatureRequest,
        user: {
          primarySignatureGuid: user.primarySignatureGuid,
          settings: {
            firstName: user.settings.firstName,
            lastName: user.settings.lastName,
          },
        },
        isEmbedded,
        isQualifiedSignature:
          tsmGuid.includes('eid-at-finalize') ||
          tsmGuid.includes('eid-pending'),
        allowDecline: tsmGuid.includes('decline'),
        canInsertEverywhere,
        allowColorSignature,
        allowReassign: tsmGuid.includes('default'),
        defaultDateFormat,
        allowedSignatureTypes: {
          C: true,
          T: true,
          U: true,
          E: true,
          S: true,
          ERROR: false,
        },
        defaultSignatureType: 'C',
      };

      if (tsmGuid.includes('embedded-redirect')) {
        signatureRequestObject.isEmbedded = true;
        signatureRequestObject.embeddedRedirectUrl =
          'https://example.com/embedded';
      }

      /**
       * When fetching too early, our backend does exactly what you would
       * expect. Just return a request with no or fields :facepalm:
       */
      const ready = this.readyRequests[tsmGuid]?.().ready ?? false;
      if (!ready) {
        signatureRequest.pages = [];
        signatureRequest.fields = [];
        signatureRequest.attachmentSlots = [];
        signatureRequest.rules = [];
      }

      if (tsmGuid.includes('eid-pending')) {
        signatureRequestObject.isQualifiedSignature = true;
        signatureRequestObject.qualifiedSignatureProperties = {
          qesUrl: 'some-url',
          eidProviderClientId: '1234',
          eidCountryCode: 'US',
        };
        signatureRequest.pages = [];
        signatureRequest.fields = [];
      }

      if (tsmGuid.includes('prefill')) {
        signatureRequest.fields = simpleFields;
        signatureRequest.signedData = {
          'simple-number': {
            type: 'inputtextbox',
            data: {
              value: '42',
            },
          },
        };
      }

      return {
        ...signatureRequestObject,
        // These have been added to dpc-signer-app, but are not yet available in
        // the OAS.  As they get added, TypeScript is going to demand they move up
        // to the other object.
        esignDisclosure,
        isComingFromSmsDelivery: false,
        transmissionGuid: tsmGuid,
        requestTitle: 'Residential Real Estate Lease',
      };
    };

  savedData: Record<string, Record<string, FieldSave>> = {};

  signerAppSaveActionRequest: SignerAppApi['signerAppSaveActionRequest'] =
    async ({ signatureRequestId, savedData }) => {
      const tsmGuid = getTsmFromSignatureRequestId(signatureRequestId);
      const errorStatus = /(\d{3})-error-on-save/.exec(tsmGuid)?.[1];
      if (errorStatus) {
        const errorStatusToMessage: Record<string, string> = {
          400: 'Generic error',
          401: 'credentials_required',
          403: "We couldn't authenticate your request, try refreshing the page",
          500: 'Server broke',
        };

        throw new ResponseError(
          new Response(
            JSON.stringify({
              error: errorStatusToMessage[errorStatus] || 'Unknown error',
            }),
            { status: Number(errorStatus) },
          ),
          'Response returned an error code',
        );
      }

      this.savedData[signatureRequestId] = savedData;
    };

  async signerAppDeclineAction({
    declineReason: reason,
  }: DeclineArgs): Promise<DeclineResponse> {
    return new Promise((resolve, reject) => {
      if (reason === 'reject') {
        reject();
      } else if (reason.startsWith('redirect-to:')) {
        resolve({ success: true, redirectUrl: reason.slice(12) });
      } else {
        resolve({ success: true, redirectUrl: '' });
      }
    });
  }

  async reassign(
    _tsmGuid: string,
    _newSignerName: string,
    _newSignerEmail: string,
    _reason: string,
  ): Promise<{ success: true }> {
    return { success: true };
  }

  async uploadAttachment(
    _attachmentSlotId: string,
    _args: { file: Blob; csrfToken: string },
  ) {
    return {
      success: true,
    };
  }

  async esignDisclosureOptIn(args: signerApi.EsignDisclosureOptInArgs) {
    if (args.transmissionGuid.includes('error')) {
      throw new Error('Test Error');
    }
  }

  signerAppFinalizeAction: SignerAppApi['signerAppFinalizeAction'] = async ({
    guid,
  }) => {
    const baseResponse = {
      isValid: true,
      isTrusted: false,
      writeBack: true,
      redirectUrl: 'https://example.com/redirect',
    };

    const errorStatus = /(\d{3})-error-on-finalize/.exec(guid)?.[1];
    if (errorStatus) {
      const errorStatusToMessage: Record<string, string> = {
        400: 'Generic error',
        401: 'credentials_required',
        403: "We couldn't authenticate your request, try refreshing the page",
        404: 'Document not found',
        410: 'This document has already been signed',
        500: 'Server broke',
      };

      throw new ResponseError(
        new Response(
          JSON.stringify({
            error: errorStatusToMessage[errorStatus] || 'Unknown error',
          }),
          { status: Number(errorStatus) },
        ),
        'Response returned an error code',
      );
    }

    if (guid.includes('eid-at-finalize')) {
      return {
        ...baseResponse,
        isQualifiedSignature: true,
        qesUrl: 'some-url',
        eidProviderClientId: '1234',
        eidCountryCode: 'US',
      };
    }

    if (guid.includes('finalize-trusted-internal-redirect-with-write-back')) {
      return {
        ...baseResponse,
        isTrusted: true,
        writeBack: true,
        redirectUrl: 'https://app.hellosign.com',
      };
    }

    if (guid.includes('finalize-untrusted-external-redirect')) {
      return {
        ...baseResponse,
        isTrusted: false,
        redirectUrl: 'https://example.com/redirect',
      };
    }

    if (guid.includes('finalize-trusted-external-redirect')) {
      return {
        ...baseResponse,
        isTrusted: true,
        redirectUrl: 'https://example.com/redirect',
      };
    }

    if (guid.includes('finalize-postsign')) {
      return {
        ...baseResponse,
        isTrusted: false,
        writeBack: false,
        redirectUrl: 'https://example.com/redirect',
        postsignPageType: 'free',
        cacheKey: '1234',
      };
    }

    return baseResponse;
  };

  async getTemplateLinkInfo(guid: string, _options: { signal: AbortSignal }) {
    const chance = new Chance(guid);

    return {
      isInPerson: guid.includes('inPerson'),
      shortGuid: guid,
      templateLinkTitle: chance.sentence({ words: 5 }),
      verifyEmail: guid.includes('verifyEmail'),
    };
  }

  getOrCreateAccountForTemplateLink: SignerAppApi['getOrCreateAccountForTemplateLink'] =
    async (request, _initOverrides?: any) => {
      await simulateNetworkDelay();

      return {
        success: true,
        emailAddress: request.email,
      };
    };
}
